import { Plus, Settings2, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  defaultModelFromPromptVersion,
  engineOverrideToPromptData,
  fallbackPromptData,
  ModelComparisonSelection,
  normalizeLabel,
  promptDataToEngineOverride,
  promptVersionToPromptData,
} from "../modelComparisonTypes";

import { usePromptInitialInputs } from "./hooks/usePromptInitialInputs";

import { ModelParameters } from "@/components/ModelParameters";
import { ProviderIcon } from "@/components/ProviderWithLogo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PromptBlueprint } from "@/types";
import { PromptVersion } from "@/types/apiGetters";

interface ModelComparisonPresetSetupProps {
  promptVersion?: PromptVersion;
  isSubmitting: boolean;
  onSubmit: (models: ModelComparisonSelection[], initialInputs: Record<string, string>) => void;
}

const NEW_MODEL_EDITOR_ID = "__new_model__";

const makeSelectionId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const parameterSummary = (parameters?: Record<string, unknown>) => {
  if (!parameters) {
    return null;
  }

  const enabledParameters = Object.entries(parameters).filter(([, value]) => {
    if (value === null || value === undefined || value === "") {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  });

  if (!enabledParameters.length) {
    return null;
  }

  return enabledParameters
    .slice(0, 2)
    .map(([key, value]) => `${key}: ${typeof value === "object" ? "custom" : String(value)}`)
    .join(" · ");
};

const modelDetails = (selection: ModelComparisonSelection) => {
  const provider = selection.engine.provider ?? "Custom provider";
  const summary = parameterSummary(selection.engine.parameters);

  return summary ? `${provider} · ${summary}` : provider;
};

const ModelComparisonPresetSetup = ({
  promptVersion,
  isSubmitting,
  onSubmit,
}: ModelComparisonPresetSetupProps) => {
  const [selections, setSelections] = useState<ModelComparisonSelection[]>([]);
  const [editingSelectionId, setEditingSelectionId] = useState<string | null>(null);
  const [editingPromptData, setEditingPromptData] = useState<
    Partial<PromptBlueprint> | undefined
  >();
  const { inputVariables, initialInputs, setInitialInputs } = usePromptInitialInputs(promptVersion);

  useEffect(() => {
    const defaultModel = defaultModelFromPromptVersion(promptVersion);
    setSelections(defaultModel ? [defaultModel] : []);
  }, [promptVersion]);

  const validSelections = useMemo(
    () => selections.filter(selection => normalizeLabel(selection.engine.model)),
    [selections],
  );

  const openNewModelEditor = () => {
    const seedPromptData =
      promptVersionToPromptData(promptVersion) ||
      (selections[0] ? engineOverrideToPromptData(selections[0].engine) : fallbackPromptData());

    setEditingSelectionId(NEW_MODEL_EDITOR_ID);
    setEditingPromptData(seedPromptData);
  };

  const openEditModelEditor = (selection: ModelComparisonSelection) => {
    setEditingSelectionId(selection.id);
    setEditingPromptData(engineOverrideToPromptData(selection.engine));
  };

  const closeEditor = () => {
    setEditingSelectionId(null);
    setEditingPromptData(undefined);
  };

  const saveEditor = () => {
    if (!editingSelectionId || !editingPromptData) {
      return;
    }

    const engine = promptDataToEngineOverride(editingPromptData);
    if (!engine.model.trim()) {
      return;
    }

    if (editingSelectionId === NEW_MODEL_EDITOR_ID) {
      setSelections(previous => [
        ...previous,
        {
          id: makeSelectionId("model"),
          label: engine.model,
          engine,
        },
      ]);
      closeEditor();
      return;
    }

    setSelections(previous =>
      previous.map(selection => {
        if (selection.id !== editingSelectionId) {
          return selection;
        }

        const shouldUpdateLabel =
          normalizeLabel(selection.label) === normalizeLabel(selection.engine.model);

        return {
          ...selection,
          label: shouldUpdateLabel ? engine.model : selection.label,
          engine,
        };
      }),
    );
    closeEditor();
  };

  const removeSelection = (id: string) => {
    setSelections(previous => previous.filter(selection => selection.id !== id));
  };

  const updateLabel = (id: string, label: string) => {
    setSelections(previous =>
      previous.map(selection => (selection.id === id ? { ...selection, label } : selection)),
    );
  };

  const selectedEngineModelName = promptDataToEngineOverride(editingPromptData).model;
  const canCreate = validSelections.length >= 2;
  const footerText =
    validSelections.length < 2 ? "Add one more model." : `${validSelections.length} models`;

  const isPromptChat = useMemo(() => {
    const promptTemplate = promptVersion?.prompt_template;
    return Boolean(
      promptTemplate &&
        typeof promptTemplate === "object" &&
        "type" in promptTemplate &&
        promptTemplate.type === "chat",
    );
  }, [promptVersion?.prompt_template]);

  const chatMessages = useMemo(() => {
    const promptTemplate = promptVersion?.prompt_template;
    if (
      !(
        promptTemplate &&
        typeof promptTemplate === "object" &&
        "messages" in promptTemplate &&
        Array.isArray(promptTemplate.messages)
      )
    ) {
      return undefined;
    }

    return promptTemplate.messages
      .map(message => {
        if (!message || typeof message !== "object" || !("content" in message)) {
          return "";
        }

        return typeof message.content === "string" ? message.content : "";
      })
      .filter(Boolean);
  }, [promptVersion?.prompt_template]);

  return (
    <div className="flex min-h-0 flex-col">
      <div className="max-h-[58vh] overflow-y-auto px-4 py-0 sm:px-5">
        {inputVariables.length > 0 && (
          <div className="border-b border-gray-100 py-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-950">First row</p>
              <span className="text-xs text-gray-400">Optional</span>
            </div>
            <div className="space-y-1.5">
              {inputVariables.map(variable => (
                <div
                  key={variable}
                  className="grid gap-1.5 sm:grid-cols-[120px_minmax(0,1fr)] sm:items-center sm:gap-2"
                >
                  <label
                    className="truncate text-xs text-gray-500"
                    htmlFor={`model-comparison-input-${variable}`}
                  >
                    {variable}
                  </label>
                  <Input
                    id={`model-comparison-input-${variable}`}
                    className="h-8 border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
                    value={initialInputs[variable] ?? ""}
                    onChange={event =>
                      setInitialInputs(previous => ({
                        ...previous,
                        [variable]: event.target.value,
                      }))
                    }
                    placeholder="Value"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1.5 py-3">
          {selections.map(selection => (
            <div
              key={selection.id}
              className="group rounded-lg border border-gray-200 bg-white px-3 py-2.5 transition-colors hover:border-gray-300"
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-50 ring-1 ring-gray-100">
                  <ProviderIcon provider={selection.engine.provider ?? ""} className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <Input
                    className="h-6 border-0 bg-transparent px-0 text-sm font-medium text-gray-950 shadow-none focus-visible:ring-0"
                    value={selection.label}
                    onChange={event => updateLabel(selection.id, event.target.value)}
                    aria-label="Column name"
                  />
                  <p className="truncate text-xs leading-4 text-gray-500">
                    {modelDetails(selection)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-gray-400 hover:text-gray-900"
                  onClick={() => openEditModelEditor(selection)}
                  aria-label="Edit model"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-gray-400 hover:text-gray-900"
                  onClick={() => removeSelection(selection.id)}
                  aria-label="Remove model"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={openNewModelEditor}
            className="flex h-10 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-200 text-sm text-gray-500 transition hover:border-gray-400 hover:bg-gray-50 hover:text-gray-800"
          >
            <Plus className="h-3.5 w-3.5" />
            Add model
          </button>
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="text-xs text-gray-500">{footerText}</p>
          <Button
            type="button"
            onClick={() => onSubmit(selections, initialInputs)}
            disabled={isSubmitting || !canCreate}
          >
            {isSubmitting ? "Creating…" : "Create and run"}
          </Button>
        </div>
      </div>

      <Dialog open={Boolean(editingSelectionId)} onOpenChange={isOpen => !isOpen && closeEditor()}>
        <DialogContent className="max-h-[88vh] w-[calc(100vw-1rem)] max-w-2xl overflow-y-auto p-0 sm:w-[calc(100vw-2rem)]">
          <div className="border-b border-gray-100 px-4 py-4 sm:px-6 sm:py-5">
            <DialogHeader>
              <DialogTitle className="text-base font-semibold text-gray-950">
                {editingSelectionId === NEW_MODEL_EDITOR_ID ? "Add model" : "Edit model"}
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="px-4 py-4 sm:px-6 sm:py-5">
            <ModelParameters
              className="w-full justify-between"
              isPromptChat={isPromptChat}
              displayAsPopover={false}
              shouldShowClearButton={false}
              promptData={editingPromptData}
              onUpdate={setEditingPromptData}
              messages={chatMessages}
            />
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3 sm:px-6 sm:py-4">
            <Button type="button" variant="ghost" onClick={closeEditor}>
              Cancel
            </Button>
            <Button type="button" onClick={saveEditor} disabled={!selectedEngineModelName.trim()}>
              Save model
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModelComparisonPresetSetup;
